import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSearch, FiBookmark, FiMenu, FiX, FiUser, FiLogOut,
  FiShield, FiChevronDown, FiMic, FiFilm,
} from 'react-icons/fi';
import { useMovieContext } from '../context/MovieContext';
import { useAuth } from '../context/AuthContext';
import { searchMovies, IMG_BASE } from '../api/tmdb';

const Navbar = () => {
  const [scrolled,     setScrolled]     = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [query,        setQuery]        = useState('');
  const [suggestions,  setSuggestions]  = useState([]);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [userOpen,     setUserOpen]     = useState(false);
  const [listening,    setListening]    = useState(false);
  const { watchlist }                   = useMovieContext();
  const { user, isAdmin, logOut }       = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const userRef   = useRef();
  const timerRef  = useRef();
  const recognRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const fn = (e) => { if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      searchMovies(query).then(r => setSuggestions(r.data.results.slice(0, 6))).catch(() => {});
    }, 280);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  // ── Voice search ────────────────────────────────────────────────
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice search not supported in this browser.'); return; }
    if (listening) { recognRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    recognRef.current = rec;
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setQuery(text);
      setSearchOpen(true);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
    setListening(true);
    setSearchOpen(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate('/search?q=' + encodeURIComponent(query));
    setQuery(''); setSuggestions([]); setSearchOpen(false);
  };

  const goSuggestion = (id) => {
    navigate('/movie/' + id);
    setSearchOpen(false); setQuery(''); setSuggestions([]);
  };

  const handleLogout = async () => {
    setUserOpen(false);
    await logOut();
    navigate('/');
  };

  const navLinks = [
    { label: 'Home',      path: '/' },
    { label: 'Movies',    path: '/movies' },
    { label: 'Watchlist', path: '/watchlist' },
    { label: 'Request',   path: '/request' },
  ];

  const avatar   = user?.photoURL;
  const initials = (user?.displayName || user?.email || 'U')[0].toUpperCase();

  return (
    <motion.nav initial={{ y: -80 }} animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(10,10,15,0.97)' : 'linear-gradient(to bottom,rgba(10,10,15,0.85),transparent)',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.04)' : 'none',
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">

        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <motion.div whileHover={{ scale: 1.04 }} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--red)' }}>
              <span className="font-display text-white text-sm leading-none">C</span>
            </div>
            <span className="font-display text-2xl tracking-widest hidden sm:block" style={{ color: 'var(--text)' }}>
              CINESPUR
            </span>
          </motion.div>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map(l => (
            <Link key={l.path} to={l.path}>
              <motion.span whileHover={{ color: '#E50914' }}
                className="font-body text-sm font-medium transition-colors relative"
                style={{ color: location.pathname === l.path ? 'var(--red)' : 'var(--muted)' }}>
                {l.label}
                {location.pathname === l.path && (
                  <motion.span layoutId="underline"
                    className="absolute -bottom-1 left-0 right-0 h-px block"
                    style={{ background: 'var(--red)' }} />
                )}
              </motion.span>
            </Link>
          ))}
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-2">

          {/* Search + voice */}
          <div className="relative">
            <AnimatePresence>
              {searchOpen && (
                <motion.form initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 260, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                  onSubmit={handleSearch}
                  className="absolute right-16 top-1/2 -translate-y-1/2">
                  <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
                    placeholder={listening ? '🎤 Listening…' : 'Search movies…'}
                    className="w-full px-4 py-2 rounded-full text-sm font-body outline-none"
                    style={{ background: 'rgba(28,28,40,0.97)', border: '1px solid rgba(229,9,20,0.4)', color: 'var(--text)' }} />
                  {suggestions.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                      className="absolute top-full mt-2 w-full rounded-2xl overflow-hidden shadow-2xl z-50"
                      style={{ background: 'rgba(13,13,20,0.99)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      {suggestions.map(m => (
                        <div key={m.id} onClick={() => goSuggestion(m.id)}
                          className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white/5 transition-colors">
                          {m.poster_path
                            ? <img src={IMG_BASE + '/w92' + m.poster_path} alt="" className="w-8 h-11 object-cover rounded flex-shrink-0" />
                            : <div className="w-8 h-11 rounded flex-shrink-0 flex items-center justify-center" style={{ background: 'var(--card)' }}><FiFilm size={14} style={{ color: 'var(--muted)' }} /></div>
                          }
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{m.title}</p>
                            <p className="text-xs" style={{ color: 'var(--muted)' }}>{m.release_date?.split('-')[0]}</p>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </motion.form>
              )}
            </AnimatePresence>

            {/* Voice mic */}
            <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={startVoice}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              style={{ color: listening ? 'var(--red)' : 'var(--muted)' }}
              title="Voice search">
              {listening ? <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 0.8 }}><FiMic size={17} /></motion.span> : <FiMic size={17} />}
            </motion.button>

            <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={() => { setSearchOpen(s => !s); setQuery(''); setSuggestions([]); }}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              style={{ color: 'var(--text)' }}>
              {searchOpen ? <FiX size={19} /> : <FiSearch size={19} />}
            </motion.button>
          </div>

          {/* Watchlist */}
          <Link to="/watchlist">
            <motion.div whileHover={{ scale: 1.1 }}
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              style={{ color: 'var(--text)' }}>
              <FiBookmark size={19} />
              {watchlist.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center font-accent"
                  style={{ background: 'var(--red)', color: '#fff', fontSize: 9 }}>
                  {watchlist.length > 9 ? '9+' : watchlist.length}
                </span>
              )}
            </motion.div>
          </Link>

          {/* User avatar / sign-in */}
          {user ? (
            <div className="relative" ref={userRef}>
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                onClick={() => setUserOpen(o => !o)}
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full glass hover:bg-white/10 transition-colors">
                {avatar
                  ? <img src={avatar} alt="" className="w-7 h-7 rounded-full object-cover ring-2" style={{ ringColor: 'var(--red)' }} />
                  : <div className="w-7 h-7 rounded-full flex items-center justify-center font-display text-xs"
                      style={{ background: 'var(--red)', color: '#fff' }}>{initials}</div>
                }
                <span className="hidden sm:block text-xs font-body max-w-[80px] truncate" style={{ color: 'var(--text)' }}>
                  {user.displayName || user.email.split('@')[0]}
                </span>
                <motion.div animate={{ rotate: userOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <FiChevronDown size={13} style={{ color: 'var(--muted)' }} />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {userOpen && (
                  <motion.div initial={{ opacity: 0, y: -8, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.94 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-52 rounded-2xl overflow-hidden shadow-2xl z-50"
                    style={{ background: 'rgba(13,13,20,0.99)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{user.displayName || 'User'}</p>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted)' }}>{user.email}</p>
                    </div>

                    {[
                      isAdmin && { to: '/admin',   icon: FiShield, label: 'Admin Panel',  color: 'var(--red)' },
                      { to: '/profile', icon: FiUser,   label: 'My Profile' },
                      { to: '/watchlist', icon: FiBookmark, label: 'Watchlist' },
                    ].filter(Boolean).map(item => (
                      <Link key={item.to} to={item.to} onClick={() => setUserOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors cursor-pointer">
                          <item.icon size={14} style={{ color: item.color || 'var(--muted)' }} />
                          <span className="text-sm font-body" style={{ color: 'var(--text)' }}>{item.label}</span>
                        </div>
                      </Link>
                    ))}

                    <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-900/20 transition-colors">
                        <FiLogOut size={14} style={{ color: 'var(--muted)' }} />
                        <span className="text-sm font-body" style={{ color: 'var(--muted)' }}>Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => navigate('/auth')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-medium"
              style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 18px rgba(229,9,20,0.35)' }}>
              <FiUser size={14} /> Sign In
            </motion.button>
          )}

          {/* Mobile hamburger */}
          <button className="md:hidden p-2 hover:bg-white/10 rounded-full transition-colors"
            style={{ color: 'var(--text)' }} onClick={() => setMenuOpen(m => !m)}>
            {menuOpen ? <FiX size={21} /> : <FiMenu size={21} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="md:hidden overflow-hidden glass border-t"
            style={{ borderColor: 'var(--border)' }}>
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map(l => (
                <Link key={l.path} to={l.path} onClick={() => setMenuOpen(false)}>
                  <span className="font-body text-sm font-medium"
                    style={{ color: location.pathname === l.path ? 'var(--red)' : 'var(--text)' }}>
                    {l.label}
                  </span>
                </Link>
              ))}
              {isAdmin && (
                <Link to="/admin" onClick={() => setMenuOpen(false)}>
                  <span className="font-body text-sm font-medium flex items-center gap-2" style={{ color: 'var(--red)' }}>
                    <FiShield size={14} /> Admin Panel
                  </span>
                </Link>
              )}
              {user
                ? <>
                    <Link to="/profile" onClick={() => setMenuOpen(false)}>
                      <span className="text-sm font-body flex items-center gap-2" style={{ color: 'var(--text)' }}>
                        <FiUser size={14} /> My Profile
                      </span>
                    </Link>
                    <button onClick={() => { handleLogout(); setMenuOpen(false); }}
                      className="text-left text-sm font-body flex items-center gap-2"
                      style={{ color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <FiLogOut size={14} /> Sign Out
                    </button>
                  </>
                : <button onClick={() => { navigate('/auth'); setMenuOpen(false); }}
                    className="text-left text-sm font-body font-medium flex items-center gap-2"
                    style={{ color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <FiUser size={14} /> Sign In
                  </button>
              }
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
